import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'
import { coursePeriod } from '../courses/period.js'
import { ApplyModal } from '../components/ApplyModal.jsx'

// Public course detail: description, schedule, capacity, registration window,
// and the "Apply a participant" button → its own page (no modals).

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export function CourseDetailPage() {
  const { t } = useTranslation()
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showApply, setShowApply] = useState(false)
  const [applyMode, setApplyMode] = useState('enroll') // 'enroll' | 'waitlist'
  const [error, setError] = useState(null)

  function openApply(mode = 'enroll') {
    if (!authLoading && !user) {
      // Come back to this course with the picker open once logged in.
      navigate('/login', { state: { from: { pathname: location.pathname, search: '?inscriere=1' } } })
      return
    }
    setApplyMode(mode)
    setShowApply(true)
  }

  useEffect(() => {
    setLoading(true); setError(null)
    api.courses.get(slug)
      .then((d) => setCourse(d.course))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [slug])

  // Shareable enrollment link (`?inscriere=1`, copied from Admin → Courses):
  // open the picker straight away. The param is dropped once used so closing
  // the picker or reloading does not reopen it.
  useEffect(() => {
    if (!course || authLoading || searchParams.get('inscriere') !== '1') return
    if (course.managed === 'self') return
    if (!course.is_open && !course.waitlist_open) return
    if (user) setSearchParams({}, { replace: true })
    openApply(course.is_open ? 'enroll' : 'waitlist')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, authLoading, user])

  if (loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (error || !course) {
    return (
      <main className="container">
        <p className="error">{t('courses.errors.not_found')}</p>
        <p><Link to="/courses">{t('courses.back_to_list')}</Link></p>
      </main>
    )
  }

  const c = course
  return (
    <main className="container">
      <p><Link to="/courses">← {t('courses.back_to_list')}</Link></p>
      <h1>{c.featured === 1 && '★ '}{c.name}</h1>
      <p className="muted">{c.category_name}{c.teacher_name ? ` · ${t('courses.teacher')}: ${c.teacher_name}` : ''}</p>

      {c.image_url && (
        <img src={c.image_url} alt="" style={{ width: '100%', maxWidth: 480, aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px', display: 'block' }} />
      )}

      {c.description && <p>{c.description}</p>}

      <p>
        {c.price !== null
          ? <><strong>{fmtPrice(c.price)}</strong></>
          : <span className="muted">{t('courses.contact_teacher_price')}</span>}
      </p>
      {c.teacher_phone && (
        <p>{t('courses.teacher_phone', { phone: c.teacher_phone })}</p>
      )}

      {coursePeriod(t, c.date_start, c.date_end) && (
        <p className="muted">{coursePeriod(t, c.date_start, c.date_end)}</p>
      )}
      {c.registration_end && (
        <p className="muted">{t('courses.registration_until', { date: c.registration_end })}</p>
      )}
      {c.is_open && c.days_to_register !== null && c.days_to_register <= 5 && (
        <p className="error">{t('courses.days_left', { count: c.days_to_register })}</p>
      )}
      {c.capacity_left !== null && (
        <p className="muted">{t('courses.seats_left', { count: c.capacity_left })}</p>
      )}

      {(c.schedules || []).length > 0 && (
        <>
          <h2>{t('courses.schedule_title')}</h2>
          <ul>
            {c.schedules.map((s) => (
              <li key={s.id}>
                {t(`courses.day_${DAY_KEYS[s.day_of_week]}`)} {String(s.time).slice(0, 5)}
                {s.notes ? ` · ${s.notes}` : ''}
              </li>
            ))}
          </ul>
          <p className="muted small">
            {t(`courses.repeat_${c.repeat_type}`, { count: c.repeat_period })}
          </p>
        </>
      )}

      {c.managed !== 'self' && (
        c.is_open ? (
          <p><button type="button" onClick={() => openApply('enroll')}>{t('courses.apply')}</button></p>
        ) : c.waitlist_open ? (
          // Every seat is taken but the waiting list is on: queue up instead.
          <>
            <p className="muted">{t('courses.full_waitlist')}</p>
            <p>
              <button type="button" onClick={() => openApply('waitlist')}>
                {t('courses.waitlist_join')}
              </button>
            </p>
          </>
        ) : (
          <p className="muted">{t('courses.registration_closed')}</p>
        )
      )}

      {showApply && (
        <ApplyModal course={c} mode={applyMode} onClose={() => setShowApply(false)} />
      )}
    </main>
  )
}
